import store from 'store';

import { api } from '../api';

import type { RivalGroupType, RivalType } from 'api/api.types';
import type { Merge } from 'type-fest';

type UpdateRivalGroupsParams = {
  id: number;
  addRivals: number[];
  removeRivals: number[];
};

type UpdateRivalGroupsResponse = {
  rivals: RivalType[];
};

type CreateRivalGroupParams = {
  name: string;
  rivals?: number[];
};

// `RivalGroupType.rivals` is `number[]`; the API returns hydrated
// `RivalType[]` when rivals are passed on create and a bare id list
// otherwise. `Merge` widens the property in one place — an intersection
// would silently narrow to `never`.
type CreateRivalGroupResponse = Merge<
  RivalGroupType,
  { rivals: RivalType[] | number[] }
>;

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    updateRivalGroup: build.mutation<
      UpdateRivalGroupsResponse,
      UpdateRivalGroupsParams
    >({
      query: ({ id, ...body }) => ({
        url: `rival_groups/${id}`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: async ({ id }, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          store.dispatch.rivalGroups.updateRivalGroupById({
            id: id.toString(),
            data: {
              rivals: data.rivals.map((rival) => rival.id),
            },
          });
        } catch {
          // Do nothing
        }
      },
    }),
    createRivalGroup: build.mutation<
      CreateRivalGroupResponse,
      CreateRivalGroupParams
    >({
      query: (body) => ({
        url: `rival_groups.json`,
        method: 'POST',
        body,
      }),
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          // The API returns `rivals` as either RivalType[] (when passed
          // rivals on create) or a bare id list — normalise to number[] on
          // the Rematch model.
          const rivalIds = Array.isArray(data.rivals)
            ? data.rivals.map((r) =>
                typeof r === 'number' ? r : (r as RivalType).id,
              )
            : [];
          store.dispatch.rivalGroups.populate({
            rivalGroups: {
              [data.id.toString()]: {
                ...data,
                rivals: rivalIds,
              } as RivalGroupType,
            },
          });
        } catch {
          // Do nothing — caller handles UX for failures.
        }
      },
    }),
  }),
});

export const { useUpdateRivalGroupMutation, useCreateRivalGroupMutation } =
  extendedApi;
